export type RoundingMode = 'HALF_UP' | 'HALF_EVEN';

const USD = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

/** Format integer cents as $X,XXX.XX */
export function formatUSD(cents: number): string {
  return USD.format(cents / 100);
}

/** Round a JS number to integer cents using HALF_UP (away from 0 at .5) */
export function numberToCents(n: number, mode: RoundingMode = 'HALF_UP'): number {
  // IMPORTANT: round the *dollar* value to 2 dp first, then convert to cents. This prevents high precision drift.
  const twoDp = (() => {
    const s = n.toFixed(3);
    const v = Number(s);
    return (mode === 'HALF_EVEN')
      ? Number((Math.round((v + Number.EPSILON) * 100) / 100).toFixed(2))
      : Number(v.toFixed(2)); // HALF_UP behavior via toFixed
  })();
  return Math.round(twoDp * 100);
}

export function percentOfCents(baseCents: number, pct: number): number {
  const value = baseCents * pct;
  return Math.round(value);
}

export function parsePercent(p: string): number {
  const s = p.trim().replace('%', '');
  return Number(s) / 100;
}

export interface DrawLineIn {
  event: string;
  percentage?: string;
  fixedPct?: number;
}

export interface DrawLineOut {
  event: string;
  percentage: string;
  amountCents: number;
  total: string;
}

export function allocateDraws(
  totalDollars: number,
  lines: DrawLineIn[],
): DrawLineOut[] {
  const totalCents = numberToCents(totalDollars, 'HALF_UP');

  const remainderIndex = lines.findIndex(l => (l.percentage || '').toLowerCase() === 'remainder');
  const fixedLines = lines
    .map((l, i) => ({...l, idx: i}))
    .filter(l => l.idx !== remainderIndex);

  type Tmp = { idx: number; event: string; pct: number; raw: number; cents: number; frac: number; }
  const provisional: Tmp[] = fixedLines.map(l => {
    const pct = typeof l.fixedPct === 'number' ? l.fixedPct
             : (l.percentage ? parsePercent(l.percentage) : 0);
    const raw = totalCents * pct; // raw cents before rounding
    const cents = Math.round(raw); // HALF_UP to nearest cent
    const frac = raw - Math.floor(raw); // fractional part for largest remainder method
    return { idx: l.idx, event: l.event, pct, raw, cents, frac };
  });

  let allocated = provisional.reduce((s, x) => s + x.cents, 0);
  let leftover = totalCents - allocated;

  const out: DrawLineOut[] = new Array(lines.length).fill(null as any);

  if (remainderIndex >= 0) {
    provisional.forEach(x => {
      out[x.idx] = {
        event: lines[x.idx].event,
        percentage: (lines[x.idx].percentage ?? (x.pct * 100).toFixed(2) + '%'),
        amountCents: x.cents,
        total: formatUSD(x.cents),
      };
    });
    out[remainderIndex] = {
      event: lines[remainderIndex].event,
      percentage: 'Remainder',
      amountCents: leftover,
      total: formatUSD(leftover),
    };
  } else {
    const order = provisional
      .slice()
      .sort((a, b) => leftover >= 0 ? (b.frac - a.frac) : (a.frac - b.frac));

    let i = 0;
    while (leftover !== 0) {
      const target = order[i % order.length];
      target.cents += leftover > 0 ? 1 : -1;
      leftover += leftover > 0 ? -1 : 1;
      i++;
    }

    provisional.forEach(x => {
      out[x.idx] = {
        event: lines[x.idx].event,
        percentage: (lines[x.idx].percentage ?? (x.pct * 100).toFixed(2) + '%'),
        amountCents: x.cents,
        total: formatUSD(x.cents),
      };
    });
  }

  return out;
}
